<template>
	<SidePanelContent :type="SidebarContent.EVENT_TYPE_LEGEND" with-close-bar hide-on-click-outside>
		<template #heading>{{ t('legend.header') }}</template>

		<div class="sectionHeader">{{ t('legend.opportunityDesc') }}</div>

		<div class="legendBox">
			<LegendEventRow :description="t('legend.opportunity')">
				<OpportunityEvent :event="opportunityEvent" :is-selected="false" />
			</LegendEventRow>

			<LegendEventRow :description="t('legend.eventFromOpportunity')">
				<DiscountEvent :event="fromOpportunityEvent" :is-selected="false" />
			</LegendEventRow>
		</div>

		<div class="sectionHeader">{{ t('legend.userCreatedDesc') }}</div>

		<div class="legendBox">
			<LegendEventRow :description="t('legend.planned')">
				<DiscountEvent :event="plannedEvent" :is-selected="false" />
			</LegendEventRow>

			<LegendEventRow :description="t('legend.scheduled')">
				<DiscountEvent :event="scrapedEvent" :is-selected="false" />
			</LegendEventRow>
		</div>

		<div class="sectionHeader">{{ t('legend.storeEventDesc') }}</div>

		<div class="legendBox">
			<LegendEventRow :description="t('legend.storeOpportunity')">
				<StoreOpportunityEvent :event="storeOpportunityEvent" :is-selected="false" />
			</LegendEventRow>

			<LegendEventRow :description="t('legend.storePlanned')">
				<DiscountEvent :event="storePlannedEvent" :is-selected="false" />
			</LegendEventRow>

			<LegendEventRow :description="t('legend.storeScheduled')">
				<DiscountEvent :event="storeScheduledEvent" :is-selected="false" />
			</LegendEventRow>
		</div>

		<div class="sectionHeader">{{ t('legend.cooldownsAndIndicationsDesc') }}</div>

		<div class="legendBox">
			<LegendEventRow :description="t('legend.eventWithNote')">
				<DiscountEvent :event="eventWithNote" :is-selected="false" />
			</LegendEventRow>

			<LegendEventRow :description="t('legend.cooldownForward')">
				<CooldownEvent :event="cooldownForward" :is-selected="false" />
			</LegendEventRow>

			<LegendEventRow :description="t('legend.cooldownBackward')">
				<NoGoEvent :event="cooldownBackward" :is-selected="false" />
			</LegendEventRow>

			<div class="flex items-start gap-04">
				<WarningIcon class="mt-01" />
				<div class="font-body-s whitespace-pre-wrap text-normal">{{ t('legend.warningDesc') }}</div>
			</div>
		</div>

		<div class="sectionHeader">{{ t('legend.tooltipsDesc') }}</div>

		<div class="legendBox">
			<div class="flex w-[25rem] flex-col gap-06">
				<div class="flex flex-col">
					<OpportunityEvent :event="opportunityEvent" :is-selected="false" />

					<LegendTooltip :event="opportunityEvent">
						<div class="text-message-success"><I18n path="opportunity.title" /></div>

						<template #footer>
							<div class="mt-03 w-[21rem] text-active"><I18n path="opportunity.desc" /></div>
						</template>
					</LegendTooltip>
				</div>

				<div class="flex flex-col">
					<DiscountEvent :event="plannedEventSuboptimal" :is-selected="false" />
					<LegendTooltip :event="plannedEventSuboptimal">
						<div class="flex gap-02">
							<WarningIcon />
							<div class="text-strong">Custom sale (planned)</div>
						</div>
					</LegendTooltip>
				</div>
			</div>
		</div>

		<template #footer>
			<div class="flex justify-end">
				<Button size="s" @click="closeSidebar(SidebarContent.EVENT_TYPE_LEGEND)">close</Button>
			</div>
		</template>
	</SidePanelContent>
</template>

<script lang="ts" setup>
import { type StoreName } from '@bluebrick/discounts-service-module'
import { useTranslation, Button, I18n } from '@bluebrick/saas-ui'
import { DateTime } from 'luxon'
import { ref } from 'vue'
import WarningIcon from '@/common/components/WarningIcon.vue'
import CooldownEvent from '@/features/events/components/timeline/events/CooldownEvent.vue'
import DiscountEvent from '@/features/events/components/timeline/events/DiscountEvent.vue'
import NoGoEvent from '@/features/events/components/timeline/events/NoGoEvent.vue'
import OpportunityEvent from '@/features/events/components/timeline/events/OpportunityEvent.vue'
import StoreOpportunityEvent from '@/features/events/components/timeline/events/StoreOpportunityEvent.vue'
import LegendEventRow from '@/features/sidePanel/components/LegendEventRow.vue'
import LegendTooltip from '@/features/sidePanel/components/LegendTooltip.vue'
import SidePanelContent from '@/features/sidePanel/components/SidePanelContent.vue'
import { useSidebarStore } from '@/features/sidePanel/useSidebarStore'
import { type OpportunityOrigin } from '@/schema'
import { type EventDataWithStore, EventType, SidebarContent } from '@/types'

const { closeSidebar } = useSidebarStore()
const { t } = useTranslation()

const startDate = DateTime.fromJSDate(new Date()).setZone('utc').startOf('day')
const endDate = DateTime.fromJSDate(new Date()).setZone('utc').plus({ day: 14 }).startOf('day')

const baseEventData: EventDataWithStore = {
	hash: '123',
	store_name: 'steam',
	unique_sku_id: 'xxx',
	end_date: endDate,
	start_date: startDate,
	storeName: 'steam' as StoreName,
	event_type: EventType.Discount,
	triggers_cooldown: true,
	discount_event_type: 'discount'
}

const baseOpportunityData: OpportunityOrigin = {
	name: 'name',
	start_date: startDate,
	end_date: endDate,
	discount_depth: 45,
	opportunity_type: 'calculated'
}

const opportunityEvent: EventDataWithStore = {
	...baseEventData,
	opportunity_type: 'calculated',
	event_type: EventType.Opportunity
}

const fromOpportunityEvent: EventDataWithStore = {
	...baseEventData,
	name: t('legend.sampleFromOpportunityName'),
	event_type: EventType.Discount,
	event_origin: 'customer_created',
	opportunity_origin: { ...baseOpportunityData, opportunity_type: 'calculated' },
	discount_depth: 45
}

const plannedEvent: EventDataWithStore = {
	...baseEventData,
	name: t('legend.sampleCustomerCreatedName'),
	event_type: EventType.Discount,
	event_origin: 'customer_created',
	discount_depth: 45
}

const plannedEventSuboptimal = ref<EventDataWithStore>({
	...plannedEvent,
	recommended_discount_depth: 50
})

const scrapedEvent: EventDataWithStore = {
	...baseEventData,
	name: t('legend.sampleCustomerCreatedName'),
	event_type: EventType.Discount,
	event_origin: 'scraped',
	discount_depth: 45
}

const eventWithNote: EventDataWithStore = {
	...baseEventData,
	name: t('legend.sampleCustomerCreatedName'),
	event_type: EventType.Discount,
	event_origin: 'scraped',
	discount_depth: 45,
	notes: [{ id: 'note-id', create_time: startDate, text: 'text' }]
}

const storeOpportunityEvent: EventDataWithStore = {
	...baseEventData,
	opportunity_type: 'store',
	event_type: EventType.StoreOpportunity,
	name: t('legend.sampleStoreEventName')
}

const storePlannedEvent: EventDataWithStore = {
	...baseEventData,
	event_type: EventType.Discount,
	event_origin: 'customer_created',
	opportunity_origin: { ...baseOpportunityData, opportunity_type: 'store' },
	name: t('legend.sampleStoreEventName'),
	discount_depth: 45
}

const storeScheduledEvent: EventDataWithStore = {
	...baseEventData,
	event_type: EventType.Discount,
	event_origin: 'scraped',
	opportunity_origin: { ...baseOpportunityData, opportunity_type: 'store' },
	name: t('legend.sampleStoreEventName'),
	discount_depth: 45
}

const cooldownForward: EventDataWithStore = {
	...baseEventData,
	event_type: EventType.Cooldown
}

const cooldownBackward: EventDataWithStore = {
	...baseEventData,
	event_type: EventType.NoGo
}
</script>

<style lang="scss" scoped>
@import '@bluebrick/saas-ui/saas-ui.css';

.timelineEvent,
.discountEvent {
	height: 1.625rem;
}

.legendBox {
	@apply pointer-events-none mt-03 flex flex-col gap-05 p-05;
	border: solid 0.0625rem theme('colors.ink.6');
}

.sectionHeader {
	@apply font-body-s-bold mt-05 text-active;
}
</style>
